import { motion, useInView, useReducedMotion } from "framer-motion";
import {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type RevealDirection = "up" | "down" | "left" | "right" | "none";

type RevealProps = {
  children: ReactNode;
  direction?: RevealDirection;
  distance?: number;
  delay?: number;
  duration?: number;
  once?: boolean;
  amount?: number;
};

export function Reveal({
  children,
  direction = "up",
  distance = 24,
  delay = 0,
  duration = 0.5,
  once = true,
  amount = 0.2,
}: RevealProps) {
  const reduceMotion = useReducedMotion();
  const ref = useRef<HTMLDivElement | null>(null);
  const isInView = useInView(ref, { once, amount });
  const [initialInView, setInitialInView] = useState(false);
  const [hasEnteredView, setHasEnteredView] = useState(false);
  const didMeasureRef = useRef(false);

  useLayoutEffect(() => {
    if (didMeasureRef.current) {
      return;
    }
    didMeasureRef.current = true;
    const el = ref.current;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    const viewportH = window.innerHeight || 0;
    const inViewport = rect.top < viewportH && rect.bottom > 0;
    setInitialInView(inViewport);
    if (inViewport) {
      setHasEnteredView(true);
    }
  }, []);

  useEffect(() => {
    if (isInView) {
      setHasEnteredView(true);
    }
  }, [isInView]);

  const hidden = useMemo(() => {
    if (direction === "none") {
      return { opacity: 0 };
    }

    const d = distance;
    switch (direction) {
      case "up":
        return { opacity: 0, y: d };
      case "down":
        return { opacity: 0, y: -d };
      case "left":
        return { opacity: 0, x: d };
      case "right":
        return { opacity: 0, x: -d };
      default:
        return { opacity: 0, y: d };
    }
  }, [direction, distance]);

  if (reduceMotion) {
    return <div>{children}</div>;
  }

  const visible = { opacity: 1, x: 0, y: 0 };
  const animateTarget = isInView || (once && hasEnteredView) ? visible : hidden;
  const initialTarget = initialInView ? visible : hidden;

  return (
    <motion.div
      ref={ref}
      initial={initialTarget}
      animate={animateTarget}
      transition={{ duration, delay, ease: [0.22, 1, 0.36, 1] }}
      style={{ width: "100%" }}
    >
      {children}
    </motion.div>
  );
}
